(() => {
  'use strict';

  const TIME_ZONE = 'Asia/Dubai';
  const CUTOFF_HOUR = 16;
  const CUTOFF_MINUTE = 0;
  const MORNING_CUTOFF_HOUR = 9;
  const MORNING_CUTOFF_MINUTE = 0;
  const AUTOSAVE_DELAY = 250;

  // Add holiday or blackout dates here in YYYY-MM-DD format.
  const blockedDates = [
  ];

  /** Pads a date or time number to two digits. */
  function pad(value) {
    return String(value).padStart(2, '0');
  }

  /** Returns calendar and clock parts for the supplied instant in Dubai time. */
  function getDubaiParts(date = new Date()) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(date).reduce((accumulator, part) => {
      accumulator[part.type] = part.value;
      return accumulator;
    }, {});

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
    };
  }

  /** Formats Dubai date parts as YYYY-MM-DD. */
  function formatPartsAsDate(parts) {
    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  }

  /** Returns today's YYYY-MM-DD date in Dubai. */
  function getDubaiDateString(date = new Date()) {
    return formatPartsAsDate(getDubaiParts(date));
  }

  /** Adds a number of days to a YYYY-MM-DD date string. */
  function addDaysToDateString(dateString, days) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  }

  /** Returns true when the supplied date string is listed as unavailable. */
  function isBlockedDate(dateString) {
    return blockedDates.includes(dateString);
  }

  /** Reads editable copy from the scheduler markup with a safe fallback. */
  function getSchedulerText(scheduler, key, fallback) {
    return scheduler.dataset[key] || fallback;
  }

  /** Renders the countdown text without interpreting editable copy as HTML. */
  function renderCountdown(countdown, message, detail) {
    const copy = countdown.querySelector('[data-delivery-countdown-copy]');
    const target = copy || countdown;
    target.textContent = '';
    target.append(document.createTextNode(message));

    if (detail) {
      const detailElement = document.createElement('small');
      detailElement.textContent = detail;
      target.append(detailElement);
    }
  }

  /** Returns true when same-day delivery is still open in Dubai. */
  function isBeforeCutoff(parts = getDubaiParts()) {
    return parts.hour < CUTOFF_HOUR || (parts.hour === CUTOFF_HOUR && parts.minute < CUTOFF_MINUTE);
  }

  /** Returns true when same-day morning delivery is closed in Dubai. */
  function isMorningCutoffPassed(parts = getDubaiParts()) {
    return parts.hour > MORNING_CUTOFF_HOUR || (parts.hour === MORNING_CUTOFF_HOUR && parts.minute >= MORNING_CUTOFF_MINUTE);
  }

  /** Returns the number of whole minutes remaining before the Dubai cutoff. */
  function getMinutesUntilCutoff(parts = getDubaiParts()) {
    if (!isBeforeCutoff(parts)) return 0;
    return Math.max(0, (CUTOFF_HOUR - parts.hour) * 60 + (CUTOFF_MINUTE - parts.minute));
  }

  /** Formats one time unit with the correct singular or plural label. */
  function formatUnit(value, unit) {
    return `${value} ${unit}${value === 1 ? '' : 's'}`;
  }

  /** Formats remaining minutes as an hours-and-minutes countdown label. */
  function formatCountdownDuration(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${formatUnit(hours, 'hour')} ${formatUnit(minutes, 'minute')}`;
  }

  /** Finds the first selectable date on or after the supplied date string. */
  function getNextAvailableDate(dateString) {
    let candidate = dateString;
    for (let index = 0; index < 370; index += 1) {
      if (!isBlockedDate(candidate)) return candidate;
      candidate = addDaysToDateString(candidate, 1);
    }
    return dateString;
  }

  /** Returns the current minimum delivery date for Dubai business rules. */
  function getMinimumDeliveryDate() {
    const parts = getDubaiParts();
    const today = formatPartsAsDate(parts);
    const baseDate = isBeforeCutoff(parts) ? today : addDaysToDateString(today, 1);
    return getNextAvailableDate(baseDate);
  }

  /** Returns a friendly label for a delivery date string. */
  function getDeliveryDateLabel(dateString) {
    const tomorrow = addDaysToDateString(getDubaiDateString(), 1);
    if (dateString === tomorrow) return 'Tomorrow';

    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }

  /** Formats a YYYY-MM-DD value for the stable visible date control. */
  function formatDateInputValue(dateString) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString || '')) return '';

    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).formatToParts(date).reduce((accumulator, part) => {
      accumulator[part.type] = part.value;
      return accumulator;
    }, {});

    return `${parts.day} ${parts.month} ${parts.year}`;
  }

  /** Keeps the visual date shell synchronized with the native input value. */
  function updateDateDisplay(dateControl, dateInput) {
    const display = dateControl?.querySelector('[data-delivery-date-display]');
    if (display) display.textContent = formatDateInputValue(dateInput?.value);
  }

  /** Writes an inline error message and updates ARIA state. */
  function setFieldError(field, errorElement, message) {
    if (!field || !errorElement) return;
    field.setAttribute('aria-invalid', 'true');
    errorElement.textContent = message;
    errorElement.classList.add('is-visible');
  }

  /** Clears an inline error message and ARIA invalid state. */
  function clearFieldError(field, errorElement) {
    if (!field || !errorElement) return;
    field.removeAttribute('aria-invalid');
    errorElement.textContent = '';
    errorElement.classList.remove('is-visible');
  }

  /** Updates the optional card-message and delivery-instructions character counts. */
  function updateCharacterCount(scheduler) {
    const counters = [
      {
        field: scheduler.querySelector('[data-delivery-card-message], [data-delivery-notes]'),
        counter: scheduler.querySelector('[data-delivery-card-message-count], [data-delivery-notes-count]'),
      },
      {
        field: scheduler.querySelector('[data-delivery-instructions]'),
        counter: scheduler.querySelector('[data-delivery-instructions-count]'),
      },
    ];

    counters.forEach(({ field, counter }) => {
      if (field && counter) counter.textContent = String(field.value.length);
    });
  }

  /** Returns all delivery time radio inputs in one scheduler. */
  function getTimeInputs(scheduler) {
    return Array.from(scheduler.querySelectorAll('[data-delivery-time]'));
  }

  /** Returns the selected delivery time radio input. */
  function getSelectedTimeInput(scheduler) {
    return scheduler.querySelector('[data-delivery-time]:checked:not(:disabled)');
  }

  /** Returns true when an input is the morning delivery slot. */
  function isMorningTimeInput(input) {
    return input?.dataset.deliveryTimeSlot === 'morning' || input?.value === 'Morning (Before 12:00 PM)';
  }

  /** Returns true when morning is unavailable for the currently selected date. */
  function shouldDisableMorningTime(scheduler) {
    const dateInput = scheduler.querySelector('[data-delivery-date]');
    return Boolean(dateInput?.value && dateInput.value === getDubaiDateString() && isMorningCutoffPassed());
  }

  /** Applies disabled state and messaging to a delivery time card. */
  function setTimeSlotDisabled(input, isDisabled, message = '') {
    const slot = input.closest('.delivery-scheduler__slot');
    input.disabled = isDisabled;
    input.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

    if (!slot) return;
    slot.classList.toggle('is-disabled', isDisabled);
    if (isDisabled && message) {
      slot.setAttribute('title', message);
    } else {
      slot.removeAttribute('title');
    }
  }

  /** Disables same-day morning delivery after the 9 AM Dubai cutoff. */
  function syncTimeSlotConstraints(scheduler) {
    const disableMorning = shouldDisableMorningTime(scheduler);
    const disabledMessage = getSchedulerText(scheduler, 'morningCutoffText', 'Morning delivery is unavailable after 9:00 AM Dubai time for same-day orders.');
    const timeHelp = scheduler.querySelector('[data-delivery-time-help]');
    let clearedSelection = false;

    getTimeInputs(scheduler).forEach((input) => {
      const isDisabled = isMorningTimeInput(input) && disableMorning;
      setTimeSlotDisabled(input, isDisabled, disabledMessage);

      if (isDisabled && input.checked) {
        input.checked = false;
        clearedSelection = true;
      }
    });

    if (timeHelp) {
      timeHelp.textContent = disableMorning ? disabledMessage : '';
      timeHelp.hidden = !disableMorning;
    }

    updateTimeSlotState(scheduler);
    return clearedSelection;
  }

  /** Applies or clears invalid state on the delivery time card group. */
  function setTimeInvalidState(scheduler, isInvalid) {
    getTimeInputs(scheduler).forEach((input) => {
      if (isInvalid) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    });
  }

  /** Updates visual state classes for the delivery time cards. */
  function updateTimeSlotState(scheduler) {
    getTimeInputs(scheduler).forEach((input) => {
      const slot = input.closest('.delivery-scheduler__slot');
      if (!slot) return;
      slot.classList.toggle('is-selected', input.checked && !input.disabled);
    });
  }

  /** Updates the countdown panel from the current Dubai time. */
  function updateCountdown(scheduler) {
    const countdown = scheduler.querySelector('[data-delivery-countdown]');
    if (!countdown) return;

    const parts = getDubaiParts();
    if (isBeforeCutoff(parts)) {
      const remainingMinutes = getMinutesUntilCutoff(parts);
      const openText = getSchedulerText(scheduler, 'countdownOpenText', 'Order within [time] for same-day delivery.');
      const afterText = getSchedulerText(scheduler, 'countdownAfterText', 'Orders placed after 4:00 PM will be delivered the next available day.');
      countdown.classList.remove('is-closed');
      renderCountdown(countdown, openText.replace('[time]', formatCountdownDuration(remainingMinutes)), afterText);
      return;
    }

    const nextAvailableDate = getMinimumDeliveryDate();
    const closedText = getSchedulerText(scheduler, 'countdownClosedText', 'Same-day delivery is closed.');
    const nextText = getSchedulerText(scheduler, 'countdownNextText', 'Next available delivery: [date].');
    countdown.classList.add('is-closed');
    renderCountdown(countdown, closedText, nextText.replace('[date]', getDeliveryDateLabel(nextAvailableDate)));
  }

  /** Applies min-date rules and clears impossible selections. */
  function syncDateConstraints(scheduler) {
    const dateInput = scheduler.querySelector('[data-delivery-date]');
    if (!dateInput) return;

    const minimumDate = getMinimumDeliveryDate();
    dateInput.min = minimumDate;

    if (dateInput.value && (dateInput.value < minimumDate || isBlockedDate(dateInput.value))) {
      dateInput.value = '';
    }

    if (!isBeforeCutoff() && !dateInput.value) {
      dateInput.value = minimumDate;
    }

    updateDateDisplay(scheduler.querySelector('.delivery-scheduler__date-control'), dateInput);
  }

  /** Validates the date field against required, cutoff, and blocked-date rules. */
  function validateDate(scheduler) {
    const dateInput = scheduler.querySelector('[data-delivery-date]');
    const dateError = scheduler.querySelector('[data-delivery-date-error]');
    const minimumDate = getMinimumDeliveryDate();

    if (!dateInput.value) {
      setFieldError(dateInput, dateError, getSchedulerText(scheduler, 'dateRequiredText', 'Please choose a delivery date.'));
      return false;
    }

    if (dateInput.value < minimumDate) {
      setFieldError(dateInput, dateError, getSchedulerText(scheduler, 'dateMinText', 'Please choose the next available delivery date.'));
      return false;
    }

    if (isBlockedDate(dateInput.value)) {
      setFieldError(dateInput, dateError, getSchedulerText(scheduler, 'dateBlockedText', 'Delivery is unavailable on this date. Please choose another date.'));
      return false;
    }

    clearFieldError(dateInput, dateError);
    return true;
  }

  /** Validates all scheduler fields and focuses the first missing control. */
  function validateScheduler(scheduler, focusInvalid = false) {
    syncTimeSlotConstraints(scheduler);

    const dateInput = scheduler.querySelector('[data-delivery-date]');
    const selectedTimeInput = getSelectedTimeInput(scheduler);
    const timeError = scheduler.querySelector('[data-delivery-time-error]');
    const summaryError = scheduler.querySelector('[data-delivery-summary-error]');
    const validDate = validateDate(scheduler);
    let validTime = true;

    if (!selectedTimeInput) {
      setTimeInvalidState(scheduler, true);
      if (timeError) {
        timeError.textContent = getSchedulerText(scheduler, 'timeRequiredText', 'Please choose a delivery time.');
        timeError.classList.add('is-visible');
      }
      validTime = false;
    } else {
      setTimeInvalidState(scheduler, false);
      if (timeError) {
        timeError.textContent = '';
        timeError.classList.remove('is-visible');
      }
    }

    if (!validDate || !validTime) {
      if (summaryError) {
        summaryError.textContent = getSchedulerText(scheduler, 'summaryRequiredText', 'Please complete the delivery information before checkout.');
        summaryError.classList.add('is-visible');
      }
      if (focusInvalid) {
        const invalidField = !validDate ? dateInput : getTimeInputs(scheduler).find((input) => !input.disabled) || getTimeInputs(scheduler)[0];
        invalidField?.focus({ preventScroll: false });
      }
      return false;
    }

    if (summaryError) {
      summaryError.textContent = '';
      summaryError.classList.remove('is-visible');
    }
    return true;
  }

  /** Collects scheduler field values as Shopify cart attributes. */
  function collectAttributes(scheduler) {
    const cardMessage = scheduler.querySelector('[data-delivery-card-message], [data-delivery-notes]')?.value || '';

    return {
      'Delivery Date': scheduler.querySelector('[data-delivery-date]')?.value || '',
      'Delivery Time': getSelectedTimeInput(scheduler)?.value || '',
      'Delivery Notes': cardMessage,
      'Delivery Instructions': scheduler.querySelector('[data-delivery-instructions]')?.value || '',
    };
  }

  /** Opens the native picker from any click inside the date control. */
  function bindDatePicker(dateControl, dateInput) {
    if (!dateControl || !dateInput) return;

    dateControl.addEventListener('click', (event) => {
      if (dateInput.disabled || dateInput.readOnly || typeof dateInput.showPicker !== 'function') return;

      try {
        dateInput.focus({ preventScroll: true });
        dateInput.showPicker();
        event.preventDefault();
      } catch {
        // The browser keeps its default date-input behavior when showPicker is unavailable.
      }
    });
  }

  /** Shows or hides the optional delivery-instructions field. */
  function setInstructionsVisibility(scheduler, isOpen, focusField = false) {
    const container = scheduler.querySelector('[data-delivery-instructions-container]');
    const panel = scheduler.querySelector('[data-delivery-instructions-field]');
    const trigger = scheduler.querySelector('[data-delivery-instructions-trigger]');
    const triggerLabel = trigger?.querySelector('[data-delivery-instructions-trigger-label]');

    if (!container || !panel || !trigger) return;

    container.classList.toggle('is-open', isOpen);
    panel.hidden = !isOpen;
    trigger.setAttribute('aria-expanded', String(isOpen));

    if (triggerLabel) {
      triggerLabel.textContent = isOpen
        ? trigger.dataset.removeLabel || 'Hide delivery instructions'
        : trigger.dataset.addLabel || 'Add delivery instructions';
    }

    trigger.querySelector('svg')?.classList.toggle('is-open', isOpen);

    if (focusField) {
      scheduler.querySelector('[data-delivery-instructions]')?.focus({ preventScroll: false });
    }
  }

  /** Binds the optional delivery-instructions disclosure control. */
  function bindInstructions(scheduler) {
    const trigger = scheduler.querySelector('[data-delivery-instructions-trigger]');
    const panel = scheduler.querySelector('[data-delivery-instructions-field]');
    const instructions = scheduler.querySelector('[data-delivery-instructions]');

    if (!trigger || !panel) return;

    setInstructionsVisibility(scheduler, !panel.hidden || Boolean(instructions?.value));
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      setInstructionsVisibility(scheduler, !isOpen, !isOpen);
    });
  }

  /** Saves scheduler values to Shopify cart attributes through the Ajax API. */
  function saveAttributes(scheduler) {
    const attributes = collectAttributes(scheduler);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    }).catch((error) => {
      console.error('Delivery scheduler cart attribute save failed:', error);
    });
  }

  /** Creates a debounced wrapper around a function. */
  function debounce(callback, delay) {
    let timerId;
    return (...args) => {
      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => callback(...args), delay);
    };
  }

  /** Connects one scheduler instance to its form and controls. */
  function bindScheduler(scheduler) {
    if (scheduler.dataset.deliverySchedulerInitialized === 'true') return;
    scheduler.dataset.deliverySchedulerInitialized = 'true';

    const formId = scheduler.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    const dateInput = scheduler.querySelector('[data-delivery-date]');
    const dateControl = scheduler.querySelector('.delivery-scheduler__date-control');
    const timeInputs = getTimeInputs(scheduler);
    const cardMessage = scheduler.querySelector('[data-delivery-card-message], [data-delivery-notes]');
    const instructions = scheduler.querySelector('[data-delivery-instructions]');
    const saveAttributesDebounced = debounce(() => saveAttributes(scheduler), AUTOSAVE_DELAY);

    if (form) form.setAttribute('novalidate', 'novalidate');

    bindDatePicker(dateControl, dateInput);
    bindInstructions(scheduler);

    syncDateConstraints(scheduler);
    const timeSelectionCleared = syncTimeSlotConstraints(scheduler);
    updateCountdown(scheduler);
    updateCharacterCount(scheduler);
    updateTimeSlotState(scheduler);

    if ((dateInput && dateInput.value) || timeSelectionCleared) saveAttributesDebounced();

    const intervalId = window.setInterval(() => {
      if (!document.body.contains(scheduler)) {
        window.clearInterval(intervalId);
        return;
      }
      syncDateConstraints(scheduler);
      if (syncTimeSlotConstraints(scheduler)) saveAttributesDebounced();
      updateCountdown(scheduler);
    }, 60000);

    dateInput?.addEventListener('change', () => {
      syncDateConstraints(scheduler);
      const clearedSelection = syncTimeSlotConstraints(scheduler);
      if (clearedSelection) validateScheduler(scheduler);
      else validateDate(scheduler);
      saveAttributesDebounced();
    });

    dateInput?.addEventListener('input', () => {
      updateDateDisplay(dateControl, dateInput);
    });

    timeInputs.forEach((timeInput) => timeInput.addEventListener('change', () => {
      updateTimeSlotState(scheduler);
      validateScheduler(scheduler);
      saveAttributesDebounced();
    }));

    cardMessage?.addEventListener('input', () => {
      updateCharacterCount(scheduler);
      saveAttributesDebounced();
    });

    instructions?.addEventListener('input', () => {
      updateCharacterCount(scheduler);
      saveAttributesDebounced();
    });

    form?.addEventListener('submit', (event) => {
      syncDateConstraints(scheduler);
      if (!validateScheduler(scheduler, true)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      saveAttributes(scheduler);
    });
  }

  /** Initializes every scheduler currently present in the DOM. */
  function initSchedulers() {
    document.querySelectorAll('[data-delivery-scheduler]').forEach(bindScheduler);
  }

  /** Watches for Shopify-rendered cart drawer updates and initializes new schedulers. */
  function observeDynamicCartUpdates() {
    const observer = new MutationObserver(() => initSchedulers());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initSchedulers();
    observeDynamicCartUpdates();
  });

  document.addEventListener('shopify:section:load', () => {
    initSchedulers();
  });
})();
